import React, { useState, useContext, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Capacitor } from '@capacitor/core';
import { AuthContext, API } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Checkbox } from '@/components/ui/checkbox';
import { 
  Car, Mail, Lock, User, Shield, Loader2, Eye, EyeOff,
  CheckCircle2, XCircle, AlertCircle, Info, FileText
} from 'lucide-react';
import LegalDocModal from '@/components/LegalDocModal';
import PublicLegalFooter from '@/components/PublicLegalFooter';

// Validações de senha - TODAS as regras do backend
const PASSWORD_RULES = [
  { id: 'length', label: 'Mínimo 8 caracteres', test: (pwd) => pwd.length >= 8 },
  { id: 'uppercase', label: 'Pelo menos 1 letra maiúscula', test: (pwd) => /[A-Z]/.test(pwd) },
  { id: 'lowercase', label: 'Pelo menos 1 letra minúscula', test: (pwd) => /[a-z]/.test(pwd) },
  { id: 'number', label: 'Pelo menos 1 número', test: (pwd) => /[0-9]/.test(pwd) },
  { id: 'special', label: 'Pelo menos 1 caractere especial (!@#$%&*)', test: (pwd) => /[!@#$%^&*(),.?":{}|<>_\-+=\[\]\\;'`~]/.test(pwd) },
];

export default function ClientLogin() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, login } = useContext(AuthContext);
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [showResendVerification, setShowResendVerification] = useState(false);
  const [resendEmail, setResendEmail] = useState('');
  const [resendLoadingModal, setResendLoadingModal] = useState(false);
  const [resendCooldownModal, setResendCooldownModal] = useState(0);
  
  // Estados de login
  const [loginForm, setLoginForm] = useState({
    identifier: '', // CPF ou E-mail
    password: ''
  });
  
  // Estados de cadastro
  const [registerForm, setRegisterForm] = useState({
    name: '',
    cpf: '',
    email: '',
    password: '',
    confirmPassword: ''
  });
  
  // Estado do aceite de termos
  const [termsAccepted, setTermsAccepted] = useState(false);

  // LGPD — versões ativas (snapshot no momento do cadastro) + modais
  const [termsVersion, setTermsVersion] = useState('1.0');
  const [privacyVersion, setPrivacyVersion] = useState('1.0');
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [showPrivacyModal, setShowPrivacyModal] = useState(false);
  
  // Estado de verificação pendente
  const [pendingVerification, setPendingVerification] = useState(false);
  const [verificationEmail, setVerificationEmail] = useState('');
  const [resendLoading, setResendLoading] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [devVerificationUrl, setDevVerificationUrl] = useState(''); // Para modo dev

  // Redirect se já logado - verificar onboarding
  useEffect(() => {
    const checkOnboarding = async () => {
      if (user && user.user_type === 'client') {
        try {
          const token = localStorage.getItem('token');
          const res = await axios.get(`${API}/client/onboarding-status`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          
          if (res.data.onboarding_complete) {
            navigate('/client/dashboard');
          } else if (res.data.next_step === 'vehicle') {
            navigate('/client/onboarding/vehicle');
          } else if (res.data.next_step === 'plan') {
            navigate('/client/onboarding/plan');
          }
        } catch (error) {
          navigate('/client/dashboard');
        }
      }
    };
    
    checkOnboarding();
  }, [user, navigate]);

  // Processar callback do Google OAuth
  useEffect(() => {
    const processGoogleCallback = async () => {
      const sessionToken = searchParams.get('session_token');
      
      if (!sessionToken) return;
      
      setGoogleLoading(true);
      try {
        const res = await axios.post(`${API}/auth/google/client`, {
          session_token: sessionToken
        });
        
        if (res.data.token) {
          login(res.data.token, res.data.user);
          toast.success('Login realizado com sucesso!');
          navigate('/client/dashboard');
        }
      } catch (error) {
        toast.error(error.response?.data?.detail || 'Erro ao autenticar com Google');
      } finally {
        setGoogleLoading(false);
      }
    };
    
    processGoogleCallback();
  }, [searchParams, login, navigate]);

  // Cooldown timer para reenvio de e-mail
  useEffect(() => {
    if (resendCooldown > 0) {
      const timer = setTimeout(() => setResendCooldown(resendCooldown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [resendCooldown]);

  // LGPD — prefetch das versões ativas para registrar consentimento versionado
  useEffect(() => {
    const fetchActiveVersions = async () => {
      try {
        const [termsRes, privacyRes] = await Promise.all([
          axios.get(`${API}/admin/terms/active`, { params: { type: 'client' } }).catch(() => null),
          axios.get(`${API}/admin/terms/active`, { params: { type: 'privacy_policy' } }).catch(() => null),
        ]);
        if (termsRes?.data?.version) setTermsVersion(termsRes.data.version);
        if (privacyRes?.data?.version) setPrivacyVersion(privacyRes.data.version);
      } catch (e) {
        // Mantém defaults "1.0"
      }
    };
    fetchActiveVersions();
  }, []);

  // Handler para reenviar e-mail de verificação
  const handleResendVerification = async () => {
    if (resendCooldown > 0 || resendLoading) return;
    
    setResendLoading(true);
    try {
      const res = await axios.post(`${API}/auth/resend-verification`, {
        email: verificationEmail
      });
      
      toast.success(res.data.message || 'E-mail reenviado com sucesso!');
      setResendCooldown(30); // Cooldown de 30 segundos
      
      // Se estiver em modo dev, mostrar URL de verificação
      if (res.data._dev_verification_url) {
        setDevVerificationUrl(res.data._dev_verification_url);
      }
    } catch (error) {
      const detail = error.response?.data?.detail || 'Erro ao reenviar e-mail';
      
      // Se for erro de rate limit (429), extrair tempo de espera
      if (error.response?.status === 429) {
        const match = detail.match(/(\d+) segundos/);
        if (match) {
          setResendCooldown(parseInt(match[1]));
        }
      }
      
      toast.error(detail);
    } finally {
      setResendLoading(false);
    }
  };

  // Cooldown timer para modal de reenvio
  useEffect(() => {
    if (resendCooldownModal > 0) {
      const timer = setTimeout(() => setResendCooldownModal(resendCooldownModal - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [resendCooldownModal]);

  // Handler para reenviar verificação via modal (sem login)
  const handleResendVerificationModal = async () => {
    if (resendCooldownModal > 0 || resendLoadingModal) return;
    
    if (!resendEmail.trim()) {
      toast.error('Informe o e-mail');
      return;
    }
    
    setResendLoadingModal(true);
    try {
      const res = await axios.post(`${API}/auth/resend-verification`, {
        email: resendEmail.toLowerCase().trim()
      });
      
      toast.success(res.data.message || 'E-mail reenviado!');
      setResendCooldownModal(30);
      
      if (res.data._dev_verification_url) {
        toast.info('Modo desenvolvimento: verifique o console para o link');
        console.log('[DEV] Verification URL:', res.data._dev_verification_url);
      }
    } catch (error) {
      const detail = error.response?.data?.detail || 'Erro ao reenviar';
      
      if (error.response?.status === 429) {
        const match = detail.match(/(\d+) segundos/);
        if (match) {
          setResendCooldownModal(parseInt(match[1]));
        }
      }
      
      toast.error(detail);
    } finally {
      setResendLoadingModal(false);
    }
  };

  // Validar CPF
  const validateCPF = (cpf) => {
    const cpfClean = cpf.replace(/\D/g, '');
    if (cpfClean.length !== 11) return false;
    
    // Verificar CPFs inválidos conhecidos
    if (/^(\d)\1{10}$/.test(cpfClean)) return false;
    
    // Validar dígitos verificadores
    let sum = 0;
    for (let i = 0; i < 9; i++) {
      sum += parseInt(cpfClean[i]) * (10 - i);
    }
    let digit1 = 11 - (sum % 11);
    if (digit1 > 9) digit1 = 0;
    
    sum = 0;
    for (let i = 0; i < 10; i++) {
      sum += parseInt(cpfClean[i]) * (11 - i);
    }
    let digit2 = 11 - (sum % 11);
    if (digit2 > 9) digit2 = 0;
    
    return cpfClean[9] === String(digit1) && cpfClean[10] === String(digit2);
  };

  // Validar força da senha
  const getPasswordStrength = (password) => {
    return PASSWORD_RULES.map(rule => ({
      ...rule,
      valid: rule.test(password)
    }));
  };

  const isPasswordValid = (password) => {
    return PASSWORD_RULES.every(rule => rule.test(password));
  };

  // Detectar se é CPF ou E-mail
  const isEmail = (value) => value.includes('@');

  // Formatar CPF enquanto digita
  const formatCPF = (value) => {
    const cpfClean = value.replace(/\D/g, '').slice(0, 11);
    if (cpfClean.length <= 3) return cpfClean;
    if (cpfClean.length <= 6) return `${cpfClean.slice(0, 3)}.${cpfClean.slice(3)}`;
    if (cpfClean.length <= 9) return `${cpfClean.slice(0, 3)}.${cpfClean.slice(3, 6)}.${cpfClean.slice(6)}`;
    return `${cpfClean.slice(0, 3)}.${cpfClean.slice(3, 6)}.${cpfClean.slice(6, 9)}-${cpfClean.slice(9)}`;
  };

  // Handler de login
  const handleLogin = async (e) => {
    e.preventDefault();
    
    if (!loginForm.identifier) {
      toast.error('Informe o CPF ou E-mail');
      return;
    }
    
    if (!loginForm.password) {
      toast.error('Informe a senha');
      return;
    }
    
    setLoading(true);
    try {
      const res = await axios.post(`${API}/auth/login`, {
        identifier: loginForm.identifier.replace(/\D/g, '').length === 11 
          ? loginForm.identifier.replace(/\D/g, '') 
          : loginForm.identifier,
        password: loginForm.password
      });
      
      if (res.data.user.user_type !== 'client') {
        toast.error('Esta área é exclusiva para clientes. Use a área do prestador.');
        return;
      }
      
      login(res.data.token, res.data.user);
      toast.success('Login realizado com sucesso!');
      
      // Verificar status de onboarding e redirecionar apropriadamente
      try {
        const onboardingRes = await axios.get(`${API}/client/onboarding-status`, {
          headers: { Authorization: `Bearer ${res.data.token}` }
        });
        
        if (onboardingRes.data.onboarding_complete) {
          navigate('/client/dashboard');
        } else if (onboardingRes.data.next_step === 'vehicle') {
          navigate('/client/onboarding/vehicle');
        } else if (onboardingRes.data.next_step === 'plan') {
          navigate('/client/onboarding/plan');
        } else {
          navigate('/client/dashboard');
        }
      } catch (onboardingError) {
        // Se falhar ao verificar onboarding, ir para dashboard
        navigate('/client/dashboard');
      }
    } catch (error) {
      const detail = error.response?.data?.detail || 'Erro ao fazer login';
      toast.error(detail);
    } finally {
      setLoading(false);
    }
  };

  // Handler de cadastro
  const handleRegister = async (e) => {
    e.preventDefault();
    
    // Validações
    if (!registerForm.name || registerForm.name.length < 3) {
      toast.error('Nome deve ter pelo menos 3 caracteres');
      return;
    }
    
    const cpfClean = registerForm.cpf.replace(/\D/g, '');
    if (!validateCPF(cpfClean)) {
      toast.error('CPF inválido. Verifique os dígitos.');
      return;
    }
    
    if (!registerForm.email || !registerForm.email.includes('@')) {
      toast.error('E-mail inválido');
      return;
    }
    
    if (!isPasswordValid(registerForm.password)) {
      toast.error('A senha não atende aos requisitos de segurança');
      return;
    }
    
    if (registerForm.password !== registerForm.confirmPassword) {
      toast.error('As senhas não conferem');
      return;
    }
    
    if (!termsAccepted) {
      toast.error('Você deve aceitar os Termos de Uso e a Política de Privacidade para continuar');
      return;
    }
    
    setLoading(true);
    try {
      const res = await axios.post(`${API}/auth/register`, {
        name: registerForm.name,
        cpf_cnpj: cpfClean,
        email: registerForm.email.toLowerCase().trim(),
        password: registerForm.password,
        confirm_password: registerForm.confirmPassword,
        user_type: 'client',
        terms_accepted: true,
        accepted_terms_version: termsVersion,
        accepted_privacy_version: privacyVersion
      });
      
      // Login automático e redirecionamento para onboarding
      // Independente do status de verificação de e-mail
      login(res.data.token, res.data.user);
      toast.success('Cadastro realizado! Agora cadastre seu veículo.');
      navigate('/client/onboarding/vehicle');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao cadastrar');
    } finally {
      setLoading(false);
    }
  };

  // Handler Google Login
  const handleGoogleLogin = () => {
    // Em apps nativos (Capacitor) window.location.origin = https://localhost, que quebra
    // o callback do OAuth no Android/iOS. Usamos a URL HTTPS de produção como redirect
    // (mesmo padrão adotado em MigratePlan.js com Capacitor.isNativePlatform()).
    const redirectUrl = Capacitor.isNativePlatform()
      ? 'https://jatoo.app.br/client/login'
      : window.location.origin + '/client/login';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  // Loading do callback do Google
  if (googleLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center p-4">
        <Card className="w-full max-w-md p-8 text-center">
          <Loader2 className="w-12 h-12 animate-spin mx-auto text-primary mb-4" />
          <h2 className="text-xl font-semibold">Autenticando com Google...</h2>
          <p className="text-muted-foreground mt-2">Aguarde um momento</p>
        </Card>
      </div>
    );
  }

  // Tela de verificação pendente
  if (pendingVerification) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center p-4">
        <Card className="w-full max-w-md p-8 text-center">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Mail className="w-8 h-8 text-green-600" />
          </div>
          <h2 className="text-2xl font-bold mb-2">Verifique seu E-mail!</h2>
          <p className="text-muted-foreground mb-4">
            Enviamos um link de confirmação para:
          </p>
          <p className="font-semibold text-lg mb-6 bg-slate-100 py-2 px-4 rounded-lg">
            {verificationEmail}
          </p>
          
          <Alert className="mb-6 text-left">
            <Info className="h-4 w-4" />
            <AlertDescription>
              <strong>Próximos passos:</strong>
              <ol className="list-decimal ml-4 mt-2 space-y-1">
                <li>Abra sua caixa de entrada</li>
                <li>Clique no link de confirmação</li>
                <li>Volte aqui para fazer login</li>
              </ol>
            </AlertDescription>
          </Alert>
          
          {/* Link de verificação em modo dev */}
          {devVerificationUrl && (
            <Alert className="mb-4 text-left border-blue-200 bg-blue-50">
              <AlertCircle className="h-4 w-4 text-blue-600" />
              <AlertDescription className="text-blue-800">
                <strong>[Modo Desenvolvimento]</strong>
                <p className="text-xs mt-1 break-all">
                  <a 
                    href={devVerificationUrl} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="underline hover:text-blue-600"
                  >
                    Clique aqui para verificar
                  </a>
                </p>
              </AlertDescription>
            </Alert>
          )}
          
          <p className="text-sm text-muted-foreground mb-4">
            Não recebeu? Verifique a pasta de spam ou clique abaixo.
          </p>
          
          {/* Botão de reenviar e-mail */}
          <Button 
            variant="default"
            className="w-full mb-3"
            onClick={handleResendVerification}
            disabled={resendLoading || resendCooldown > 0}
            data-testid="resend-verification-btn"
          >
            {resendLoading ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
                Reenviando...
              </>
            ) : resendCooldown > 0 ? (
              <>
                <Mail className="w-4 h-4 mr-2" />
                Reenviar em {resendCooldown}s
              </>
            ) : (
              <>
                <Mail className="w-4 h-4 mr-2" />
                Reenviar e-mail de confirmação
              </>
            )}
          </Button>

          {/* Botão Continuar (modo teste/preview) */}
          <Button 
            variant="secondary"
            className="w-full mb-3 bg-amber-100 hover:bg-amber-200 text-amber-800"
            onClick={async () => {
              // Fazer login mesmo sem verificação (modo dev)
              try {
                const res = await axios.post(`${API}/auth/login`, {
                  identifier: verificationEmail,
                  password: registerForm.password
                });
                
                if (res.data.token) {
                  login(res.data.token, res.data.user);
                  toast.success('Entrando em modo teste...');
                  
                  // Verificar onboarding e redirecionar
                  try {
                    const onboardingRes = await axios.get(`${API}/client/onboarding-status`, {
                      headers: { Authorization: `Bearer ${res.data.token}` }
                    });
                    
                    if (onboardingRes.data.onboarding_complete) {
                      navigate('/client/dashboard');
                    } else {
                      navigate('/client/onboarding/vehicle');
                    }
                  } catch {
                    navigate('/client/onboarding/vehicle');
                  }
                }
              } catch (error) {
                // Se falhar, mostrar mensagem
                toast.error('Faça login manualmente');
                setPendingVerification(false);
              }
            }}
            data-testid="continue-test-mode-btn"
          >
            Continuar (modo teste)
          </Button>
          
          <Button 
            variant="outline" 
            className="w-full"
            onClick={() => {
              setPendingVerification(false);
              setDevVerificationUrl('');
              setResendCooldown(0);
            }}
          >
            Voltar para Login
          </Button>
        </Card>
      </div>
    );
  }

  // Modal de reenvio de verificação
  if (showResendVerification) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center p-4">
        <Card className="w-full max-w-md p-8">
          <div className="flex items-center justify-center mb-4">
            <Mail className="w-12 h-12 text-primary" />
          </div>
          <h2 className="text-2xl font-bold text-center mb-2">Reenviar Confirmação</h2>
          <p className="text-center text-muted-foreground mb-6">
            Informe o e-mail da conta que precisa de verificação.
          </p>
          
          <div className="space-y-4">
            <div>
              <Label htmlFor="resend-email">E-mail</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  id="resend-email"
                  type="email"
                  placeholder="seu@email.com"
                  className="pl-10"
                  value={resendEmail}
                  onChange={(e) => setResendEmail(e.target.value)}
                  data-testid="resend-modal-email-input"
                />
              </div>
            </div>
            
            <Button 
              className="w-full"
              onClick={handleResendVerificationModal}
              disabled={resendLoadingModal || resendCooldownModal > 0}
              data-testid="resend-modal-submit-btn"
            >
              {resendLoadingModal ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  Enviando...
                </>
              ) : resendCooldownModal > 0 ? (
                <>
                  <Mail className="w-4 h-4 mr-2" />
                  Aguarde {resendCooldownModal}s
                </>
              ) : (
                <>
                  <Mail className="w-4 h-4 mr-2" />
                  Enviar link de confirmação
                </>
              )}
            </Button>
            
            <Button 
              variant="ghost" 
              className="w-full"
              onClick={() => {
                setShowResendVerification(false);
                setResendEmail('');
                setResendCooldownModal(0);
              }}
            >
              Voltar para Login
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F8FAFC] flex flex-col items-center justify-center p-4">
      <Card className="w-full max-w-md p-8 border-[#E2E8F0] shadow-lg">
        {/* Header */}
        <div className="flex items-center justify-center mb-4">
          <div className="bg-[#0B1F3A] p-3 rounded-xl">
            <Car className="w-10 h-10 text-white" />
          </div>
        </div>
        <h1 className="text-3xl font-bold text-center mb-1 text-[#0B1F3A]">JATOO</h1>
        <p className="text-center text-slate-500 mb-6">Área do Cliente</p>

        <Tabs defaultValue="login" className="w-full">
          <TabsList className="grid w-full grid-cols-2 mb-6 bg-slate-100">
            <TabsTrigger value="login" data-testid="tab-login" className="data-[state=active]:bg-[#2563EB] data-[state=active]:text-white">Entrar</TabsTrigger>
            <TabsTrigger value="register" data-testid="tab-register" className="data-[state=active]:bg-[#2563EB] data-[state=active]:text-white">Cadastrar</TabsTrigger>
          </TabsList>

          {/* TAB LOGIN */}
          <TabsContent value="login">
            <form onSubmit={handleLogin} className="space-y-4">
              <div>
                <Label htmlFor="login-identifier">CPF ou E-mail</Label>
                <div className="relative">
                  {isEmail(loginForm.identifier) ? (
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  ) : (
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  )}
                  <Input
                    id="login-identifier"
                    placeholder="000.000.000-00 ou email@exemplo.com"
                    className="pl-10"
                    value={loginForm.identifier}
                    onChange={(e) => {
                      const value = e.target.value;
                      // Se não contém @, formatar como CPF
                      if (!value.includes('@') && /^\d/.test(value.replace(/\D/g, ''))) {
                        setLoginForm({ ...loginForm, identifier: formatCPF(value) });
                      } else {
                        setLoginForm({ ...loginForm, identifier: value });
                      }
                    }}
                    required
                    data-testid="login-identifier-input"
                  />
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  Digite seu CPF ou e-mail cadastrado
                </p>
              </div>
              
              <div>
                <Label htmlFor="login-password">Senha</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    id="login-password"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="••••••••"
                    className="pl-10 pr-10"
                    value={loginForm.password}
                    onChange={(e) => setLoginForm({ ...loginForm, password: e.target.value })}
                    required
                    data-testid="login-password-input"
                  />
                  <button
                    type="button"
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              {/* Links de ajuda */}
              <div className="flex justify-between items-center text-sm">
                <button
                  type="button"
                  className="text-primary hover:underline"
                  onClick={() => navigate('/client/forgot-password')}
                  data-testid="forgot-password-link"
                >
                  Esqueceu a senha?
                </button>
                <button
                  type="button"
                  className="text-muted-foreground hover:text-primary hover:underline"
                  onClick={() => setShowResendVerification(true)}
                  data-testid="resend-verification-link"
                >
                  Reenviar confirmação
                </button>
              </div>

              <Button type="submit" className="w-full bg-[#2563EB] hover:bg-[#2563EB]/90" disabled={loading} data-testid="login-submit-btn">
                {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                Entrar
              </Button>

              <div className="relative my-4">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t"></div>
                </div>
                <div className="relative flex justify-center text-xs uppercase">
                  <span className="bg-white px-2 text-muted-foreground">ou</span>
                </div>
              </div>

              <Button 
                type="button" 
                variant="outline" 
                className="w-full" 
                onClick={handleGoogleLogin}
                data-testid="google-login-btn"
              >
                <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
                Entrar com Google
              </Button>
            </form>
          </TabsContent>

          {/* TAB CADASTRO */}
          <TabsContent value="register">
            <form onSubmit={handleRegister} className="space-y-4">
              <div>
                <Label htmlFor="register-name">Nome Completo</Label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    id="register-name"
                    placeholder="Seu nome completo"
                    className="pl-10"
                    value={registerForm.name}
                    onChange={(e) => setRegisterForm({ ...registerForm, name: e.target.value })}
                    required
                    data-testid="register-name-input"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="register-cpf">CPF</Label>
                <div className="relative">
                  <Shield className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    id="register-cpf"
                    placeholder="000.000.000-00"
                    className="pl-10"
                    value={registerForm.cpf}
                    onChange={(e) => setRegisterForm({ ...registerForm, cpf: formatCPF(e.target.value) })}
                    required
                    data-testid="register-cpf-input"
                  />
                </div>
                {registerForm.cpf && registerForm.cpf.replace(/\D/g, '').length === 11 && (
                  <p className={`text-xs mt-1 flex items-center gap-1 ${validateCPF(registerForm.cpf) ? 'text-green-600' : 'text-red-600'}`}>
                    {validateCPF(registerForm.cpf) ? (
                      <><CheckCircle2 className="w-3 h-3" /> CPF válido</>
                    ) : (
                      <><XCircle className="w-3 h-3" /> CPF inválido</>
                    )}
                  </p>
                )}
              </div>
              
              <div>
                <Label htmlFor="register-email">E-mail</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    id="register-email"
                    type="email"
                    placeholder="seu@email.com"
                    className="pl-10"
                    value={registerForm.email}
                    onChange={(e) => setRegisterForm({ ...registerForm, email: e.target.value })}
                    required
                    data-testid="register-email-input"
                  />
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  Você receberá um link de confirmação
                </p>
              </div>

              <div>
                <Label htmlFor="register-password">Senha</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    id="register-password"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="Crie uma senha segura"
                    className="pl-10 pr-10"
                    value={registerForm.password}
                    onChange={(e) => setRegisterForm({ ...registerForm, password: e.target.value })}
                    required
                    data-testid="register-password-input"
                  />
                  <button
                    type="button"
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                
                {/* Requisitos de senha */}
                {registerForm.password && (
                  <div className="mt-2 space-y-1">
                    {getPasswordStrength(registerForm.password).map(rule => (
                      <div key={rule.id} className={`text-xs flex items-center gap-1 ${rule.valid ? 'text-green-600' : 'text-muted-foreground'}`}>
                        {rule.valid ? (
                          <CheckCircle2 className="w-3 h-3" />
                        ) : (
                          <XCircle className="w-3 h-3 text-red-400" />
                        )}
                        {rule.label}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div>
                <Label htmlFor="register-confirm">Repetir Senha</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    id="register-confirm"
                    type={showConfirmPassword ? 'text' : 'password'}
                    placeholder="Repita a senha"
                    className="pl-10 pr-10"
                    value={registerForm.confirmPassword}
                    onChange={(e) => setRegisterForm({ ...registerForm, confirmPassword: e.target.value })}
                    required
                    data-testid="register-confirm-input"
                  />
                  <button
                    type="button"
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  >
                    {showConfirmPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                {registerForm.confirmPassword && (
                  <p className={`text-xs mt-1 flex items-center gap-1 ${
                    registerForm.password === registerForm.confirmPassword ? 'text-green-600' : 'text-red-600'
                  }`}>
                    {registerForm.password === registerForm.confirmPassword ? (
                      <><CheckCircle2 className="w-3 h-3" /> Senhas conferem</>
                    ) : (
                      <><XCircle className="w-3 h-3" /> Senhas não conferem</>
                    )}
                  </p>
                )}
              </div>

              {/* Checkbox de Aceite dos Termos de Uso + Política de Privacidade (LGPD) */}
              <div className="flex items-start gap-3 p-3 border border-slate-200 rounded-lg bg-slate-50">
                <Checkbox
                  id="accept-terms-register"
                  checked={termsAccepted}
                  onCheckedChange={setTermsAccepted}
                  data-testid="register-terms-checkbox"
                />
                <label
                  htmlFor="accept-terms-register"
                  className="text-sm leading-relaxed cursor-pointer text-slate-700"
                >
                  <span>Li e aceito os </span>
                  <button
                    type="button"
                    className="text-primary hover:underline font-medium"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setShowTermsModal(true);
                    }}
                    data-testid="terms-link"
                  >
                    Termos de Uso
                  </button>
                  <span> e a </span>
                  <button
                    type="button"
                    className="text-primary hover:underline font-medium"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setShowPrivacyModal(true);
                    }}
                    data-testid="privacy-link"
                  >
                    Política de Privacidade
                  </button>
                  <span> da JATOO.</span>
                </label>
              </div>

              <Button 
                type="submit" 
                className="w-full" 
                disabled={loading || !isPasswordValid(registerForm.password) || registerForm.password !== registerForm.confirmPassword || !termsAccepted}
                data-testid="register-submit-btn"
              >
                {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                Criar Conta
              </Button>

              <div className="relative my-4">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t"></div>
                </div>
                <div className="relative flex justify-center text-xs uppercase">
                  <span className="bg-white px-2 text-muted-foreground">ou</span>
                </div>
              </div>

              <Button 
                type="button" 
                variant="outline" 
                className="w-full" 
                onClick={handleGoogleLogin}
                data-testid="google-register-btn"
              >
                <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
                Cadastrar com Google
              </Button>
            </form>
          </TabsContent>
        </Tabs>

        <div className="mt-6 text-center">
          <Button variant="link" onClick={() => navigate('/')} className="text-muted-foreground">
            Voltar ao início
          </Button>
        </div>
      </Card>

      {/* LGPD — Modais de Termos e Política de Privacidade */}
      <LegalDocModal
        type="client"
        open={showTermsModal}
        onOpenChange={setShowTermsModal}
        testIdPrefix="client-terms"
      />
      <LegalDocModal
        type="privacy_policy"
        open={showPrivacyModal}
        onOpenChange={setShowPrivacyModal}
        testIdPrefix="client-privacy"
      />

      {/* Rodapé público de compliance jurídico */}
      <div className="w-full max-w-md mt-6">
        <PublicLegalFooter />
      </div>
    </div>
  );
}
